import { Checkbox, Form, Input, InputNumber, Modal, Row, Select, message } from "antd";

import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { useFetchCommunicationResultItemsQuery } from "features/communicationResultItems/communicationResultItemsAPI";
import {
  useCreateCommunicationLimitMutation,
  useUpdateCommunicationLimitMutation,
} from "features/communicationLimitsTable/communicationLimitsApi";
import styled from "styled-components";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 350px;
`;

const StyledFormItem = styled(Form.Item)`
  margin-right: 12px;
`;

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
`;

export default function CommunicationLimitsModal({ record, title, open, onCancel }) {
  const [form] = Form.useForm();
  const { data: constants } = useFetchBackendConstantsQuery();

  // Fetch all enabled communication result items once, then filter by type
  const { data: allResultItems = [] } = useFetchCommunicationResultItemsQuery({
    isEnabled: true,
  });

  // Hardcoded "no result" items that should appear first in each communication result type
  const noResultItems = {
    call: { slug: "call_no_result", name: "[Call No Result]", resultType: "call" },
    text: { slug: "text_no_result", name: "[Text No Result]", resultType: "text" },
    email: { slug: "email_no_result", name: "[Email No Result]", resultType: "email" },
    voicemail: {
      slug: "voicemail_no_result",
      name: "[Voicemail No Result]",
      resultType: "voicemail",
    },
    mail: { slug: "mail_no_result", name: "[Mail No Result]", resultType: "mail" },
    communication: {
      slug: "communication_no_result",
      name: "[Communication No Result]",
      resultType: "communication",
    },
  };

  const callResultItems = [
    noResultItems.call,
    ...allResultItems.filter((item) => item.resultType === "call"),
  ];
  const textResultItems = [
    noResultItems.text,
    ...allResultItems.filter((item) => item.resultType === "text"),
  ];
  const emailResultItems = [
    noResultItems.email,
    ...allResultItems.filter((item) => item.resultType === "email"),
  ];
  const voicemailResultItems = [
    noResultItems.voicemail,
    ...allResultItems.filter((item) => item.resultType === "voicemail"),
  ];
  const mailResultItems = [
    noResultItems.mail,
    ...allResultItems.filter((item) => item.resultType === "mail"),
  ];
  const communicationResultItems = [
    noResultItems.communication,
    ...allResultItems.filter((item) => item.resultType === "communication"),
  ];

  const [createCommunicationLimit, { isLoading: createLoading }] =
    useCreateCommunicationLimitMutation();
  const [updateCommunicationLimit, { isLoading: updateLoading }] =
    useUpdateCommunicationLimitMutation();

  const onModalCancel = () => {
    onCancel();
    form.resetFields();
  };

  const onModalOk = async () => {
    const values = await form.validateFields();
    const payload = {
      ...values,
      id: record?.id,
      states: values.isAllStates ? [] : values.states,
      callResults: callResultItems
        .map((item) => item.slug)
        .filter((slug) => values.resultTags.includes(slug)),
      textResults: textResultItems
        .map((item) => item.slug)
        .filter((slug) => values.resultTags.includes(slug)),
      emailResults: emailResultItems
        .map((item) => item.slug)
        .filter((slug) => values.resultTags.includes(slug)),
      voicemailResults: voicemailResultItems
        .map((item) => item.slug)
        .filter((slug) => values.resultTags.includes(slug)),
      mailResults: mailResultItems
        .map((item) => item.slug)
        .filter((slug) => values.resultTags.includes(slug)),
      communicationResults: communicationResultItems
        .map((item) => item.slug)
        .filter((slug) => values.resultTags.includes(slug)),
    };
    const result = record
      ? await updateCommunicationLimit(payload)
      : await createCommunicationLimit({
          id: record?.id,
          ...payload,
        });

    if ("data" in result) {
      message.success(`Communication limit rule ${record ? "updated" : "created"} successfully`);
      onModalCancel();
    }
  };

  const initialValues = {
    ...record,
    states: record?.isAllStates ? [] : record?.states,
    resultTags: [
      ...(record?.callResults ?? []),
      ...(record?.textResults ?? []),
      ...(record?.emailResults ?? []),
      ...(record?.voicemailResults ?? []),
      ...(record?.mailResults ?? []),
      ...(record?.communicationResults ?? []),
    ],
  };

  return (
    <Modal
      confirmLoading={createLoading || updateLoading}
      maskClosable={false}
      title={title}
      open={open}
      onOk={onModalOk}
      onCancel={onModalCancel}
    >
      <StyledForm
        initialValues={initialValues}
        layout="vertical"
        form={form}
        validateMessages={{ required: "This is a required field" }}
      >
        <Form.Item rules={[{ required: true }]} label="Name" name="name">
          <Input />
        </Form.Item>
        <Row align="bottom">
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues?.isAllStates !== currentValues?.isAllStates
            }
          >
            {({ getFieldValue }) => {
              const checked = getFieldValue("isAllStates");
              return (
                <StyledFormItem
                  rules={[{ required: !checked }]}
                  name="states"
                  label="Debtor Location(s)"
                >
                  <Select
                    maxTagCount={1}
                    popupMatchSelectWidth={false}
                    disabled={checked}
                    mode="multiple"
                    options={[
                      {
                        label: "US States",
                        options:
                          constants?.states?.map((state) => ({
                            value: state.value,
                            label: `${state.display} (${state.value})`,
                          })) ?? [],
                      },
                      {
                        label: "Canadian Provinces",
                        options:
                          constants?.canadianProvinces?.map((province) => ({
                            value: province.value,
                            label: `${province.display} (${province.value})`,
                          })) ?? [],
                      },
                    ]}
                  />
                </StyledFormItem>
              );
            }}
          </Form.Item>
          <Form.Item valuePropName="checked" name="isAllStates">
            <Checkbox>All</Checkbox>
          </Form.Item>
        </Row>
        <Form.Item rules={[{ required: true }]} name="maxAllowed" label="# Contacts Allowed">
          <StyledInputNumber min="0" step="1" />
        </Form.Item>
        <Form.Item rules={[{ required: true }]} name="contactMethods" label="Contact Methods">
          <Select
            mode="multiple"
            popupMatchSelectWidth={false}
            options={constants?.contactMethods?.map((method) => ({
              label: method.display,
              value: method.value,
            }))}
          />
        </Form.Item>
        <Form.Item
          rules={[
            { required: true },
            ({ getFieldValue }) => ({
              validator(_, resultTags) {
                const contactMethods = getFieldValue("contactMethods") || [];

                // Get all result slugs by type
                const callResultSlugs = callResultItems.map((item) => item.slug);
                const textResultSlugs = textResultItems.map((item) => item.slug);
                const emailResultSlugs = emailResultItems.map((item) => item.slug);
                const voicemailResultSlugs = voicemailResultItems.map((item) => item.slug);
                const mailResultSlugs = mailResultItems.map((item) => item.slug);
                const communicationResultSlugs = communicationResultItems.map((item) => item.slug);

                // Check if any selected result tags are of each type
                const hasCallResults = resultTags?.some((tag) => callResultSlugs.includes(tag));
                const hasTextResults = resultTags?.some((tag) => textResultSlugs.includes(tag));
                const hasEmailResults = resultTags?.some((tag) => emailResultSlugs.includes(tag));
                const hasVoicemailResults = resultTags?.some((tag) =>
                  voicemailResultSlugs.includes(tag),
                );
                const hasMailResults = resultTags?.some((tag) => mailResultSlugs.includes(tag));
                const hasCommunicationResults = resultTags?.some((tag) =>
                  communicationResultSlugs.includes(tag),
                );

                const missingCategories = [];
                if (contactMethods.includes("phone") && !hasCallResults) {
                  missingCategories.push("Call Results");
                }
                if (contactMethods.includes("text") && !hasTextResults) {
                  missingCategories.push("Text Results");
                }
                if (contactMethods.includes("email") && !hasEmailResults) {
                  missingCategories.push("Email Results");
                }
                if (contactMethods.includes("voicemail") && !hasVoicemailResults) {
                  missingCategories.push("Voicemail Results");
                }
                if (contactMethods.includes("mail") && !hasMailResults) {
                  missingCategories.push("Mail Results");
                }
                if (!hasCommunicationResults) {
                  missingCategories.push("Communication Results");
                }

                if (missingCategories.length && resultTags.length) {
                  // We only want to display this message if the field already has values set but it's incomplete
                  return Promise.reject(
                    new Error(`Missing tags for: ${missingCategories.join(", ")}`),
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
          dependencies={["contactMethods"]}
          name="resultTags"
          label="Result Tags"
        >
          <Select
            mode="multiple"
            popupMatchSelectWidth={false}
            options={[
              {
                label: "Call Results",
                options: callResultItems.map((item) => ({
                  label: item.name,
                  value: item.slug,
                })),
              },
              {
                label: "Text Results",
                options: textResultItems.map((item) => ({
                  label: item.name,
                  value: item.slug,
                })),
              },
              {
                label: "Email Results",
                options: emailResultItems.map((item) => ({
                  label: item.name,
                  value: item.slug,
                })),
              },
              {
                label: "Voicemail Results",
                options: voicemailResultItems.map((item) => ({
                  label: item.name,
                  value: item.slug,
                })),
              },
              {
                label: "Mail Results",
                options: mailResultItems.map((item) => ({
                  label: item.name,
                  value: item.slug,
                })),
              },
              {
                label: "Communication Results",
                options: communicationResultItems.map((item) => ({
                  label: item.name,
                  value: item.slug,
                })),
              },
            ]}
          />
        </Form.Item>
        <Form.Item
          rules={[{ required: true }]}
          name="rollingPeriod"
          label="Rolling Time Period (days)"
        >
          <StyledInputNumber min="0" step="1" />
        </Form.Item>
      </StyledForm>
    </Modal>
  );
}
